<template>
    <!-- Business Info Preview -->
    <div class="business-preview" v-if="business && business.line_of_business">
        <div class="preview-icon">
            <i class="bi bi-shop"></i>
        </div>
        <div class="preview-info">
            <h4>{{ business.line_of_business }}</h4>
            <p class="text-muted">{{ business.sub_category }}</p>
        </div>
        <span class="business-type-badge" :class="business?.type?.toLowerCase()">
            {{ business?.type }}
        </span>
    </div>

    <!-- Form Content -->
    <div class="modal-form-content">
        <!-- Business Information Section -->
        <div class="form-section">
            <h5 class="section-title">
                <i class="bi bi-info-circle me-2"></i>
                Business Information
            </h5>

            <div class="row g-3">
                <!-- Business Type -->
                <div class="col-md-6">
                    <div class="form-group">
                        <label class="form-label required">
                            <i class="bi bi-tag me-1"></i>
                            Business Type
                        </label>
                        <select ref="btypeRef" class="form-select" v-model="business.type">
                            <option value="New">New</option>
                            <option v-if="apptype === 'Renewal'" value="Renewal">Renewal</option>
                        </select>
                        <small class="form-text text-muted">Select business application type</small>
                    </div>
                </div>

                <!-- Main or Other -->
                <div class="col-md-6">
                    <div class="form-group">
                        <label class="form-label required">
                            <i class="bi bi-diagram-3 me-1"></i>
                            Business Role
                        </label>
                        <select ref="mainOrOtherRef" class="form-select" v-model="business.main_or_other">
                            <option value="">Select role</option>
                            <option value="Main">Main Business</option>
                            <option value="Other">Additional Business</option>
                        </select>
                        <small class="form-text text-muted">Is this main or additional business?</small>
                    </div>
                </div>
            </div>

            <!-- Product/Services -->
            <div class="form-group mt-3">
                <label class="form-label required">
                    <i class="bi bi-briefcase me-1"></i>
                    Products / Services
                </label>
                <textarea class="form-control" v-model="business.product_services" rows="2"
                    placeholder="Describe products or services offered..."></textarea>
                <small class="form-text text-muted">List products or services (comma-separated)</small>
            </div>
        </div>

        <!-- Financial Information Section -->
        <div class="form-section">
            <h5 class="section-title">
                <i class="bi bi-cash-coin me-2"></i>
                Financial Information
            </h5>

            <div class="row g-3">
                <!-- Number of Units -->
                <div class="col-md-6">
                    <div class="form-group">
                        <label class="form-label required">
                            <i class="bi bi-123 me-1"></i>
                            Number of Units
                        </label>
                        <div class="input-group">
                            <span class="input-group-text">
                                <i class="bi bi-box"></i>
                            </span>
                            <input type="number" class="form-control text-end" v-model.number="business.no_units"
                                placeholder="0" min="0" step="1" />
                        </div>
                        <small class="form-text text-muted">Total business units</small>
                    </div>
                </div>

                <!-- Capitalization / Gross Sales -->
                <div class="col-md-6">
                    <div class="form-group">
                        <label class="form-label required">
                            <i class="bi bi-currency-exchange me-1"></i>
                            {{ business.type === 'New' ? 'Capitalization' : 'Gross Sales' }}
                        </label>
                        <div class="input-group">
                            <span class="input-group-text">₱</span>
                            <input ref="capitalizationRef" type="number" class="form-control text-end"
                                v-model.number="business.capitalization"
                                :placeholder="business.type === 'New' ? '0.00' : '0.00'" min="0" step="0.01" />
                        </div>
                        <small class="form-text text-muted">
                            {{ business.type === 'New' ? 'Total investment capital' : 'Annual gross sales' }}
                        </small>
                    </div>
                </div>
            </div>

            <!-- Amount Display -->
            <div class="amount-summary mt-3" v-if="business.capitalization > 0">
                <div class="amount-label">Total Amount:</div>
                <div class="amount-value">₱ {{ formatCurrency(business.capitalization) }}</div>
            </div>
        </div>

        <!-- Error Message -->
        <div v-if="errMsg" class="alert alert-danger mt-3">
            <div class="d-flex align-items-center">
                <i class="bi bi-exclamation-triangle me-2"></i>
                <span>{{ errMsg }}</span>
            </div>
        </div>

        <div class="row g-3">
            <button class="btn btn-outline-secondary w-100 col-lg-6 mt-2" @click="close">
                <i class="bi bi-x-circle mt-2"></i>
                Cancel
            </button>
            <button class="btn btn-danger btn-save w-100 col-lg-6 mt-2 mb-3" @click="save">
                <i class="bi bi-check-lg"></i>
                {{ mode === 'add' ? 'Add Business Line' : 'Update Business Line' }}
            </button>
        </div>


    </div>
</template>

<script setup>
import { nextTick, ref, watch } from 'vue';
// eslint-disable-next-line 
const props = defineProps({
    selectedBusiness: { type: Object, default: () => ({ type: 'New', capitalization: 0 }) },
    mode: { type: String, default: 'add' },
    apptype: { type: String, default: 'New' }
});
// eslint-disable-next-line 
const emit = defineEmits(['save','close']);

const btypeRef = ref(null);

const business = ref({ type: 'New', capitalization: 0, main_or_other: '' });
const errMsg = ref(null);
const capitalizationRef = ref(null);
const mainOrOtherRef = ref(null);

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount);
};

const close =()=>{
    emit('close')
}

const save = () => {
    errMsg.value = null;

    // Validation
    if (!business.value.main_or_other) {
        errMsg.value = 'Please specify if this is Main or Additional business.';
        nextTick(() => mainOrOtherRef.value?.focus());
        return;
    }

    if (business.value.capitalization <= 0) {
        const label = business.value.type === 'New' ? 'capitalization' : 'gross sales';
        errMsg.value = `Please provide a valid ${label} amount.`;
        nextTick(() => capitalizationRef.value?.focus());
        return;
    }

    if (!business.value.product_services?.trim()) {
        errMsg.value = 'Please describe the products or services offered.';
        return;
    }

    // Prepare data and emit
    const businessData = {
        ...business.value,
        line_of_business: business.value.description || business.value.line_of_business
    };

    emit('save', businessData);
};

const focus = () => {
    nextTick(() => {
        btypeRef.value?.focus();
    });
}
// eslint-disable-next-line
defineExpose({ focus })

watch(
    () => props.selectedBusiness,
    (newVal) => {
        if (newVal) {
            business.value = {
                type: 'New',
                capitalization: 0,
                main_or_other: '',
                ...newVal
            };
        } else {
            business.value = { type: 'New', capitalization: 0, main_or_other: '' };
        }

        errMsg.value = null;

        nextTick(() => {
            if (capitalizationRef.value) {
                capitalizationRef.value.focus();
            }
        });
    },
    { immediate: true, deep: true }
);
</script>

<style scoped>


/* Business Preview */
.business-preview {
    display: flex;
    align-items: center;
    padding: 1rem;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border-radius: 8px;
    margin-bottom: 1.5rem;
    border: 1px solid #dee2e6;
    position: relative;
}

.preview-icon {
    width: 50px;
    height: 50px;
    background: white;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 1rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.preview-icon i {
    font-size: 1.5rem;
    color: #6c757d;
}

.preview-info {
    flex: 1;
}

.preview-info h4 {
    margin: 0;
    font-weight: 600;
    color: #343a40;
    font-size: 1.1rem;
}

.preview-info p {
    margin: 0.25rem 0 0;
    font-size: 0.875rem;
}

.business-type-badge {
    position: absolute;
    top: 1rem;
    right: 1rem;
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.business-type-badge.new {
    background: #28a745;
    color: white;
}

.business-type-badge.renewal {
    background: #fd7e14;
    color: white;
}

/* Form Content */
.modal-form-content {
    max-height: 70vh;
    padding-right: 0.5rem;
}

.form-section {
    background: white;
    border-radius: 8px;
    padding: 1.25rem;
    margin-bottom: 1.25rem;
    border: 1px solid #e9ecef;
}

.section-title {
    font-weight: 600;
    color: #495057;
    margin-bottom: 1.25rem;
    padding-bottom: 0.75rem;
    border-bottom: 2px solid #f1f3f5;
    display: flex;
    align-items: center;
}

.section-title i {
    color: #6c757d;
}

/* Form Elements */
.form-label {
    font-weight: 600;
    color: #495057;
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
    font-size: 0.9rem;
}

.form-label.required::after {
    content: '*';
    color: #dc3545;
    margin-left: 0.25rem;
}

.form-label i {
    font-size: 0.875rem;
    color: #6c757d;
}

.form-select,
.form-control {
    border: 1px solid #ced4da;
    border-radius: 6px;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.form-select:focus,
.form-control:focus {
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.input-group-text {
    background-color: #f8f9fa;
    border: 1px solid #ced4da;
    color: #495057;
}

/* Amount Summary */
.amount-summary {
    background: linear-gradient(135deg, #f0f9ff 0%, #e6f7ff 100%);
    border-radius: 6px;
    padding: 1rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border: 1px solid #c5e3fc;
}

.amount-label {
    font-weight: 600;
    color: #0369a1;
    font-size: 0.95rem;
}

.amount-value {
    font-weight: 700;
    color: #0369a1;
    font-size: 1.25rem;
    letter-spacing: 0.5px;
}

/* Error Alert */
.alert-danger {
    background: linear-gradient(135deg, #fed7d7 0%, #fff5f5 100%);
    border: 1px solid #f5c6cb;
    border-left: 4px solid #dc3545;
    color: #721c24;
    padding: 0.75rem 1rem;
}

.alert-danger i {
    font-size: 1rem;
}

/* Custom Scrollbar */
.modal-form-content::-webkit-scrollbar {
    width: 6px;
}

.modal-form-content::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
}

.modal-form-content::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 10px;
}

.modal-form-content::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}

/* Responsive */
@media (max-width: 768px) {
    .business-preview {
        flex-direction: column;
        text-align: center;
        padding: 1rem;
    }

    .preview-icon {
        margin-right: 0;
        margin-bottom: 1rem;
    }

    .business-type-badge {
        position: static;
        margin-top: 0.75rem;
    }

    .section-title {
        font-size: 1rem;
    }

    .form-section {
        padding: 1rem;
    }
}
</style>